import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { ApiConfig, clientFactory, EndpointConfig } from '@/api/clientFactory'
import { documentsClient } from '@/features/documents'

import {
  APIReportTemplate,
  DocumentUpload,
  ReportFormat,
  ReportTemplate,
  ReportTemplateSchema,
  ReportType,
  reportTypes,
} from '../domain/report'
import { ReportEditSchemaType, ReportScheduleSchemaType } from '../domain/schema'

const basePath = '/api/shipping'

type CreateReportParams = {
  data: { userId?: string; scheduleMeta?: Record<string, never> } & ReportEditSchemaType
}

type EditReportParams = {
  reportID: string
  data: Pick<ReportTemplate, 'userId' | '_id'> & ReportEditSchemaType
}

type DeleteReportParams = {
  reportID: string
  data: Pick<ReportTemplate, 'userId' | '_id'>
}

type ScheduleReportParams = {
  reportID: string
  data: ReportScheduleSchemaType
}

export const reportsAPIConfig: ApiConfig<{
  getReports: EndpointConfig<unknown, APIReportTemplate[], ReportTemplate[]>
  getReport: EndpointConfig<string, APIReportTemplate, ReportTemplate>
  generateReport: EndpointConfig<{ reportId: string; format: ReportFormat | 'email' }, void>
  createReport: EndpointConfig<CreateReportParams, void>
  editReport: EndpointConfig<EditReportParams, void>
  deleteReport: EndpointConfig<DeleteReportParams, void>
  scheduleReport: EndpointConfig<ScheduleReportParams, void>
}> = {
  getReports: {
    requestConfig: () => ({
      endpoint: `${basePath}/reports`,
      method: 'get',
      deserializationStrategy: z.preprocess(
        (response) =>
          // Remove `landedCost` type reports from the list. These aren't supported in the UI.
          // TODO(Masood): remove this once the API is updated to not return these
          Array.isArray(response)
            ? response.filter((r: APIReportTemplate) => reportTypes.includes(r.type as ReportType))
            : response,
        z.array(ReportTemplateSchema)
      ),
    }),
  },
  getReport: {
    requestConfig: (id) => ({
      endpoint: `${basePath}/reports/${id}`,
      method: 'get',
      deserializationStrategy: ReportTemplateSchema,
    }),
  },
  generateReport: {
    requestConfig: ({ reportId, format }) => ({
      endpoint: `${basePath}/reports/generate/${reportId}`,
      method: 'post',
      // email format for emissions reports
      body: format === 'email' ? undefined : { format },
    }),
  },
  createReport: {
    requestConfig: ({ data }) => ({
      endpoint: `${basePath}/reports`,
      method: 'post',
      body: data,
    }),
  },
  editReport: {
    requestConfig: ({ reportID, data }) => ({
      endpoint: `${basePath}/reports/${reportID}`,
      method: 'put',
      body: data,
    }),
  },
  deleteReport: {
    requestConfig: ({ reportID, data }) => ({
      endpoint: `${basePath}/reports/${reportID}`,
      method: 'delete',
      body: data,
    }),
  },
  scheduleReport: {
    requestConfig: ({ reportID, data }) => ({
      endpoint: `${basePath}/reports/schedule/${reportID}`,
      method: 'post',
      body: data,
    }),
  },
}
export const reportsClient = clientFactory(reportsAPIConfig)

export const useCreateReport = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: reportsClient.createReport,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['reports'],
      })
    },
  })
}

export const useEditReport = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: reportsClient.editReport,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['reports'],
      })
    },
  })
}

export const useDeleteReport = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: reportsClient.deleteReport,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['reports'],
      })
    },
  })
}

export const useGenerateReport = () => {
  return useMutation({
    mutationFn: reportsClient.generateReport,
  })
}

export const useScheduleReport = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: reportsClient.scheduleReport,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['reports'],
      })
    },
  })
}

// Separate from react-query since even if the report is in the cache, we still want to make the API call to download it
export const downloadReportDocument = async (reportUploads: DocumentUpload[]) => {
  const documentUpload = reportUploads.find((u) => u.type === 'document' && u.visibility === 'external')
  if (!documentUpload) {
    throw new Error('No document found') // TODO: translate
  }
  await documentsClient.downloadDocument({ documentID: documentUpload.documentID, fileName: documentUpload.name })
}

// Using the helper so that multiple different query hooks can be used, e.g. useSuspenseQuery and ensureQueryData
export const reportsQueryOptions = () =>
  queryOptions({
    queryKey: ['reports'],
    queryFn: reportsClient.getReports,
  })

export const reportQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['reports', id],
    queryFn: () => reportsClient.getReport(id),
  })
